/**
 * useFCM Hook
 * Manages Firebase Cloud Messaging for push notifications
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { useToast } from '../contexts/ToastContext';
import { isCentreConversationNotice, isNotificationForStore } from '../utils/notificationStoreScope';
import {
  initializeMessaging,
  requestPermission,
  getFCMToken,
  registerTokenWithBackend,
  onForegroundMessage,
  getDeviceId,
  detectDeviceType
} from '../services/fcm.service';
import pushTokenService from '../services/pushToken.service';
import {
  loadStoredTokenInfo,
  saveStoredTokenInfo,
  clearStoredTokenInfo
} from '../utils/fcmTokenStorage';
import { MessagePayload } from 'firebase/messaging';
import { readNativePushToken, registerNativePushToken } from '../utils/nativePush';
import { isTwa } from '../marketing/utils/isInstalledApp';
import { getSupportAccess } from '../utils/superAdminSupportAccess';

interface UseFCMReturn {
  token: string | null;
  permission: NotificationPermission | null;
  isSupported: boolean;
  isRegistered: boolean;
  registerToken: () => Promise<void>;
}

const TOKEN_CHECK_THROTTLE_MS = 2 * 60 * 1000;

/**
 * Custom hook for Firebase Cloud Messaging
 */
export const useFCM = (): UseFCMReturn => {
  const { user, isAuthenticated } = useAuth();
  const { currentStore } = useStore();
  const { notify } = useToast();
  const currentStoreIdRef = useRef<number | null>(currentStore?.id ?? null);
  const [token, setToken] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [isSupported, setIsSupported] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const lastTokenCheckRef = useRef(0);
  const lastUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (user?.id) {
      lastUserIdRef.current = user.id;
    }
  }, [user?.id]);

  useEffect(() => {
    currentStoreIdRef.current = currentStore?.id ?? null;
  }, [currentStore?.id]);

  const syncToken = useCallback(async ({ force, reason }: { force?: boolean; reason?: string } = {}) => {
    if (!isAuthenticated || !user) {
      return;
    }

    const now = Date.now();
    if (!force && now - lastTokenCheckRef.current < TOKEN_CHECK_THROTTLE_MS) {
      return;
    }
    lastTokenCheckRef.current = now;

    let nativeRegistered = false;
    try {
      nativeRegistered = await registerNativePushToken();
      if (nativeRegistered) {
        setIsRegistered(true);
        setIsSupported(true);
        if (isTwa()) {
          const storedInfo = loadStoredTokenInfo(user.id);
          const nativeToken = readNativePushToken();
          if (storedInfo?.token && nativeToken && storedInfo.token !== nativeToken) {
            await pushTokenService.deactivateToken(storedInfo.token).catch(() => {});
            clearStoredTokenInfo(user.id);
          }
        }
      }
    } catch (error) {
      console.warn('[FCM] Native Android token not registered:', error);
    }

    try {
      if (isTwa() || !('Notification' in window) || !('serviceWorker' in navigator)) {
        if (!nativeRegistered && !readNativePushToken()) {
          setIsSupported(false);
        }
        return;
      }

      const permissionStatus = await requestPermission();
      setPermission(permissionStatus);
      if (permissionStatus !== 'granted') {
        return;
      }

      const currentToken = await getFCMToken();
      if (!currentToken) {
        return;
      }

      const deviceId = getDeviceId();
      const deviceType = detectDeviceType();
      const storedInfo = loadStoredTokenInfo(user.id);

      const registered = await registerTokenWithBackend(currentToken, {
        deviceType,
        deviceId,
        appVersion: process.env.REACT_APP_VERSION || '1.0.0'
      });

      if (!registered) {
        return;
      }

      if (storedInfo?.token && storedInfo.token !== currentToken) {
        await pushTokenService.deactivateToken(storedInfo.token).catch((error) => {
          console.warn('[FCM] ⚠️ Failed to deactivate old token after rotation:', error);
        });
      }

      saveStoredTokenInfo({
        userId: user.id,
        token: currentToken,
        deviceId,
        deviceType,
        updatedAt: Date.now()
      });
      setToken(currentToken);
      setIsRegistered(true);
    } catch (error) {
      console.error('[FCM] ❌ Error syncing token:', error);
    }
  }, [isAuthenticated, user, isRegistered]);

  // Initialize FCM when user is authenticated
  useEffect(() => {
    if (!isAuthenticated || !user) {
      console.log('[FCM] ⏭️ Skipping FCM init: not authenticated or no user');
      return;
    }

    if (getSupportAccess()?.simulatedUser) {
      return;
    }

    console.log('[FCM] 🚀 Starting FCM initialization for user:', user.id);
    const initFCM = async () => {
      try {
        if (isTwa() || !('Notification' in window) || !('serviceWorker' in navigator)) {
          await syncToken({ force: true, reason: 'init-native' });
          if (!readNativePushToken()) {
            setIsSupported(false);
          }
          return;
        }

        console.log('[FCM] ✅ Browser supports notifications');
        
        // Check current permission status
        const currentPermission = Notification.permission;
        setPermission(currentPermission);
        console.log('[FCM] 📋 Current notification permission:', currentPermission);

        // Initialize messaging
        console.log('[FCM] 🔄 Initializing Firebase Messaging...');
        const messaging = await initializeMessaging();
        if (!messaging) {
          console.error('[FCM] ❌ Failed to initialize Firebase Messaging');
          setIsSupported(false);
          return;
        }

        console.log('[FCM] ✅ Firebase Messaging initialized successfully');

        // Check user preferences - only register if push notifications are enabled
        const userPrefs = user.preferences;
        console.log('[FCM] 📋 User preferences:', {
          push_notifications: userPrefs?.push_notifications,
          in_app_notifications: userPrefs?.in_app_notifications
        });
        
        if (userPrefs && userPrefs.push_notifications !== false) {
          console.log('[FCM] 🔄 Syncing FCM token...');
          // Auto-register or refresh token
          await syncToken({ force: true, reason: 'init' });
        } else {
          console.log('[FCM] ⏭️ Push notifications disabled in user preferences, skipping token registration');
        }
      } catch (error) {
        console.error('[FCM] ❌ Error initializing FCM:', error);
        setIsSupported(false);
      }
    };

    initFCM();
  }, [isAuthenticated, user, syncToken]);


  // Handle foreground messages
  useEffect(() => {
    if (!isAuthenticated || !token) {
      console.log('[FCM] ⏭️ Skipping foreground handler setup:', {
        isAuthenticated,
        hasToken: !!token
      });
      return;
    }

    console.log('[FCM] 🔄 Setting up foreground message handler (token:', token.substring(0, 20) + '...)');
    const unsubscribe = onForegroundMessage((payload: MessagePayload) => {
      console.log('📬 [FCM FRONTEND] Foreground message received:', payload);
      const payloadType = payload.data?.type;
      if (payloadType === 'CALL_ENDED' && payload.data?.callId && 'serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((reg) => (
          Promise.all([
            `INCOMING_CALL_${payload.data?.callId}`,
            `INCOMING_CALL_voip_${payload.data?.callId}`,
            payload.data?.tag
          ].filter(Boolean).map((tag) => reg.getNotifications({ tag })))
        )).then((groups) => groups.flat().forEach((item) => item.close())).catch(() => {});
        return;
      }
      if (payloadType === 'INCOMING_CALL') {
        return;
      }

      const centreNotice = isCentreConversationNotice(payload.data);
      if (!centreNotice && !isNotificationForStore(payload.data, currentStoreIdRef.current)) {
        return;
      }
      
      // Show notification when app is in foreground
      if (payload.notification) {
        console.log('📬 [FCM FRONTEND] Showing notification:', {
          title: payload.notification.title,
          body: payload.notification.body,
          data: payload.data
        });

        if (!centreNotice) {
          notify(
            payload.notification.title || 'Nouvelle notification',
            payload.notification.body || '',
            'info'
          );
        }

        // If browser supports notifications, show native notification
        // Note: Only show if app is in foreground - background notifications are handled by Service Worker
        if ('Notification' in window && Notification.permission === 'granted') {
          // Create unique tag to match Service Worker behavior and prevent duplicates
          const tag = payload.data?.tag
            || (payload.data?.type
              ? `${payload.data.type}_${payload.data.entityType || 'general'}_${payload.data.entityId || Date.now()}`
              : `notification_${Date.now()}`);
          const title = payload.notification.title || 'Nouvelle notification';
          const body = payload.notification.body;
          const isCentrePush = payload.data?.type === 'CENTRE_MESSAGE';

          if (isCentrePush && 'serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((reg) => (
              reg.showNotification(title, {
                body,
                icon: payload.data?.icon || '/favicon.ico',
                badge: '/favicon.ico',
                tag,
                requireInteraction: true,
                renotify: true,
                vibrate: [200, 100, 200],
                data: payload.data,
                actions: [
                  { action: 'reply', title: 'Répondre' },
                  { action: 'read', title: 'Lu' }
                ]
              })
            )).catch(() => {});
          } else {
            const nativeNotification = new Notification(title, {
              body,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              tag,
              requireInteraction: true,
              renotify: true,
              vibrate: [200, 100, 200],
              data: payload.data
            });
            nativeNotification.onclick = () => {
              window.focus();
              const url = payload.data?.url;
              if (url) window.location.assign(url);
            };
          }
        } else {
          console.warn('📬 [FCM FRONTEND] Cannot create native notification:', {
            hasNotificationAPI: 'Notification' in window,
            permission: Notification.permission
          });
        }
      } else {
        console.warn('📬 [FCM FRONTEND] Message received but no notification field:', payload);
      }
    });
    
    if (!unsubscribe) {
      console.warn('📬 [FCM FRONTEND] onForegroundMessage returned null - handler not set up');
    } else {
      console.log('📬 [FCM FRONTEND] Foreground message handler registered');
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAuthenticated, token, notify]);

  // Register FCM token
  const registerToken = useCallback(async () => {
    await syncToken({ force: true, reason: 'manual' });
  }, [syncToken]);

  // Re-check token on app resume or focus
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncToken({ reason: 'visibility' });
      }
    };

    const handleFocus = () => {
      syncToken({ reason: 'focus' });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user, syncToken]);

  // Reset local FCM state when user logs out.
  // Token deactivation is handled by AuthContext.logout() (authenticated call)
  // and by api.service.ts on session expiry, so we only reset state here.
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    setToken(null);
    setIsRegistered(false);
  }, [isAuthenticated]);

  return {
    token,
    permission,
    isSupported,
    isRegistered,
    registerToken
  };
};

